import { Button } from '@repo/ui/components/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/tabs';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  FIXTURE_MIXED,
  MathJaxProvider,
  parseMarkdownToTiptap,
  RichTextEditor,
  type RichTextPayload,
  RichTextView,
} from '../components/rich-text';
import {
  resolveSandboxImage,
  revokeAllSandboxImages,
} from '../lib/rt-sandbox-adapters/resolveImageUrl';
import { loadSlot, saveSlot } from '../lib/rt-sandbox-adapters/slotStorage';
import { uploadImage } from '../lib/rt-sandbox-adapters/uploadImage';
import { useTheme } from '../lib/theme-provider';

const SANDBOX_SLOT_ID = 'sandbox-slot-1';

// Dev-only guard: redirect to / in production builds.
function devOnlyGuard() {
  if (!import.meta.env.DEV) {
    throw redirect({ to: '/' });
  }
}

export const Route = createFileRoute('/rt-sandbox')({
  beforeLoad: devOnlyGuard,
  component: RichTextSandboxRoute,
});

function getInitialValue(): RichTextPayload {
  const stored = loadSlot(SANDBOX_SLOT_ID);
  if (stored) return stored;
  // Default: start with the Tiptap empty doc
  return { format: 'tiptap.v1', body: { type: 'doc', content: [] } };
}

function RichTextSandboxRoute() {
  const [currentValue, setCurrentValue] = useState<RichTextPayload>(getInitialValue);
  const { resolved: resolvedTheme } = useTheme();

  // Revoke all sandbox Object URLs when the route unmounts to avoid memory leaks.
  useEffect(() => () => revokeAllSandboxImages(), []);

  const handleChange = useCallback((value: RichTextPayload) => {
    setCurrentValue(value);
    saveSlot(SANDBOX_SLOT_ID, value);
  }, []);

  function handleResetToMarkdown() {
    const tiptapPayload = parseMarkdownToTiptap(FIXTURE_MIXED);
    setCurrentValue({ format: 'markdown.v1', body: FIXTURE_MIXED });
    saveSlot(SANDBOX_SLOT_ID, tiptapPayload);
    requestAnimationFrame(() => setCurrentValue(tiptapPayload));
  }

  function handleClearSlot() {
    const empty: RichTextPayload = { format: 'tiptap.v1', body: { type: 'doc', content: [] } };
    saveSlot(SANDBOX_SLOT_ID, empty);
    setCurrentValue(empty);
  }

  return (
    <MathJaxProvider>
      <div className="min-h-screen bg-paper">
        {/* Header */}
        <div className="border-b border-rule bg-paper-panel px-6 py-3 flex items-center gap-4">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-bold text-[15px] text-red tracking-wide">
              rt-sandbox
            </span>
            <span className="font-mono text-[10px] text-ink-mute">DEV ONLY</span>
          </div>
          <span className="flex-1" />
          <button
            type="button"
            onClick={handleResetToMarkdown}
            className="px-3 py-1.5 text-[12px] border border-rule rounded-sm bg-paper text-ink-dim hover:text-ink hover:border-red/50 transition-colors"
          >
            Reset to Markdown source
          </button>
          <button
            type="button"
            onClick={handleClearSlot}
            className="px-3 py-1.5 text-[12px] border border-rule rounded-sm bg-paper text-ink-mute hover:text-ink hover:border-red/40 transition-colors"
          >
            Clear slot
          </button>
        </div>

        {/* Two-panel layout: editor left, render right */}
        <div className="grid grid-cols-2 gap-0 h-[calc(100vh-52px)]">
          {/* Left: editor */}
          <div className="border-r border-rule overflow-auto p-4">
            <p className="text-[11px] text-ink-mute mb-3 font-mono">EDITOR — raw LaTeX chips</p>
            <RichTextEditor
              slotId={SANDBOX_SLOT_ID}
              initialValue={currentValue}
              onChange={handleChange}
              resolveImageUrl={resolveSandboxImage}
              uploadImage={uploadImage}
              key={currentValue.format === 'tiptap.v1' ? 'tiptap' : 'md'}
            />
          </div>

          {/* Right: RENDER | PAYLOAD tabs */}
          <div className="overflow-hidden flex flex-col">
            <Tabs defaultValue="render" className="flex flex-col h-full">
              <div className="px-4 pt-3 pb-0 border-b border-rule bg-paper-panel flex items-center gap-2">
                <TabsList variant="line" className="h-8">
                  <TabsTrigger value="render" className="text-[11px] font-mono px-2 py-1">
                    RENDER
                  </TabsTrigger>
                  <TabsTrigger value="payload" className="text-[11px] font-mono px-2 py-1">
                    PAYLOAD · {(JSON.stringify(currentValue).length / 1024).toFixed(1)} KB
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="render" className="flex-1 overflow-auto m-0">
                <RichTextView
                  payload={currentValue}
                  resolveImageUrl={resolveSandboxImage}
                  theme={resolvedTheme}
                />
              </TabsContent>
              <TabsContent value="payload" className="flex-1 overflow-auto m-0">
                <div className="relative">
                  <div className="absolute top-2 right-2 z-10">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Copy payload to clipboard"
                      onClick={() => {
                        try {
                          navigator.clipboard
                            .writeText(JSON.stringify(currentValue, null, 2))
                            .then(() => toast.success('Copied'))
                            .catch(() => toast.error('Copy failed'));
                        } catch {
                          toast.error('Copy failed');
                        }
                      }}
                      className="text-[11px] font-mono h-6 px-2"
                    >
                      Copy
                    </Button>
                  </div>
                  <pre className="font-mono text-[11px] text-ink-dim bg-paper-panel p-3 rounded-sm overflow-auto whitespace-pre-wrap m-0">
                    {JSON.stringify(currentValue, null, 2)}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </MathJaxProvider>
  );
}
